import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { State } from './types';
import { endpoints, baseQuery } from 'utils/api/endpoints';

export const initialState: State = {};

const slice = createSlice({
  name: 'admins',
  initialState,
  reducers: {
    someAction(state, action: PayloadAction<any>) {},
  },
});

export const api = createApi({
  reducerPath: 'adminsApi',
  tagTypes: ['admin', 'adminsList'],
  baseQuery,
  endpoints: builder => ({
    getAdmins: builder.query<any, any>({
      query: filters => ({
        url: endpoints.ADMINS,
        method: 'GET',
        params: filters,
      }),
      providesTags: ['admin', 'adminsList'],
    }),

    getAdminById: builder.query<any, number>({
      query: id => ({
        url: `${endpoints.ADMINS}/${id}`,
        method: 'GET',
      }),
      providesTags: ['admin'],
    }),

    checkAdminUsernameOrEmail: builder.mutation<any, any>({
      query: body => ({
        url: endpoints.ADMINS + '/check-username-email',
        method: 'POST',
        body,
      }),
    }),

    createAdmin: builder.mutation({
      query: data => ({
        url: endpoints.ADMINS + '/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['admin', 'adminsList'],
    }),

    updateAdmin: builder.mutation({
      query: data => ({
        url: `${endpoints.ADMINS}/${data.id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['admin', 'adminsList'],
    }),

    deleteAdmin: builder.mutation({
      query: id => ({
        url: `${endpoints.ADMINS}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['admin', 'adminsList'],
    }),

    changeAdminPassword: builder.mutation({
      query: data => ({
        url: `${endpoints.ADMINS}/${data.id}/change-password`,
        method: 'POST',
        body: { new_password: data.new_password },
      }),
      invalidatesTags: ['admin'],
    }),

    toggleAdminStatus: builder.mutation({
      query: data => ({
        url: `${endpoints.ADMINS}/${data.id}/toggle-status`,
        method: 'POST',
        body: { status: data.status },
      }),
      invalidatesTags: ['admin', 'adminsList'],
    }),

    getAdminBackupCodes: builder.query<any, number>({
      query: id => ({
        url: `${endpoints.ADMINS}/${id}/backup-codes/download`,
        method: 'GET',
      }),
    }),
    resetAdmin2FA: builder.mutation<any, number>({
      query: id => ({
        url: `${endpoints.ADMINS}/${id}/2fa/reset`,
        method: 'POST',
      }),
    }),
  }),
});

export const { actions: AdminsActions } = slice;

export const useAdminsSlice = () => {
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: AdminsActions,
    useGetAdminsQuery: api.useLazyGetAdminsQuery,
    useGetAdminByIdQuery: api.useLazyGetAdminByIdQuery,
    useCreateAdminMutation: api.useCreateAdminMutation,
    useUpdateAdminMutation: api.useUpdateAdminMutation,
    useDeleteAdminMutation: api.useDeleteAdminMutation,
    useCheckAdminUsernameOrEmailMutation:
      api.useCheckAdminUsernameOrEmailMutation,
    useChangeAdminPasswordMutation: api.useChangeAdminPasswordMutation,
    useToggleAdminStatusMutation: api.useToggleAdminStatusMutation,
    useGetAdminBackupCodesQuery: api.useLazyGetAdminBackupCodesQuery,
    useResetAdmin2FAMutation: api.useResetAdmin2FAMutation,
  };
};
